import { useMemo, useCallback } from 'react'
import { Box, createStyles, FormControl, makeStyles, Select, MenuItem, MenuProps, Checkbox } from '@material-ui/core'
import { Colors, Pallete } from '../Colors'
import { useListLabels } from '../../../fixtures/modules/labels/hooks'
import { DocumentLabelItem } from '../DocumentLabelItem'

export interface DocumentLabelSelectProps {
  /** 選択されたラベルIDの配列 */
  selectedLabelIds?: string[]
  name?: string
  /** ラベル選択変更時のコールバック */
  onLabelChange: (labelIds: string[]) => void
}

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      background: `${Pallete.functional.background.default}`,
      '& fieldset': {
        borderRadius: '4px',
        border: `1px solid ${Colors.background.silver}`
      },
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          border: `1px solid ${Colors.success.default}`
        },
        '&:hover fieldset': {
          border: `1px solid #ababab`
        },
        '&.Mui-disabled': {
          background: Colors.background.gray
        }
      }
    },
    selectRoot: {
      minHeight: '34px',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '6px',
      paddingBottom: '6px',
      paddingLeft: '12px !important',
      '&.MuiSelect-select:focus': {
        background: `${Pallete.functional.background.default}`
      }
    },
    checkbox: {
      padding: 0,
      marginRight: '8px',
      color: Colors.base.middleGray,
      '& .MuiSvgIcon-root': {
        fontSize: '16px'
      },
      '&.Mui-checked': {
        color: Colors.accent.keyPurple.default
      }
    },
    menuRoot: {
      '&.MuiListItem-root': {
        minHeight: 0,
        zIndex: 9999,
        height: '34px',
        '&.Mui-selected': {
          background: `${Colors.background.lightGray}`
        },
        '&.Mui-focusVisible': {
          background: `${Colors.background.lightGray}`
        },
        '&.Mui-selected:hover': {
          background: `${Colors.background.lightGray}`
        },
        '&:hover': {
          background: `${Colors.background.lightGray}`
        },
        '& .MuiSvgIcon-root': {
          width: '20px',
          height: '20px'
        }
      }
    }
  })
)

export const DocumentLabelSelect: React.VFC<DocumentLabelSelectProps> = props => {
  const { selectedLabelIds = [], name, onLabelChange } = props
  const classes = useStyles(props)
  const { data: labelsData } = useListLabels()
  const labels = useMemo(() => labelsData?.labels || [], [labelsData?.labels])

  const menuProps: Partial<MenuProps> = useMemo(() => {
    return {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      getContentAnchorEl: null,
      marginThreshold: 12,
      PaperProps: {
        style: {
          marginTop: '7px',
          border: `1px solid ${Colors.accent.keyPurple.default}`,
          maxHeight: '216px',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)'
        }
      }
    }
  }, [])

  const handleChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string[]
      onLabelChange(value)
    },
    [onLabelChange]
  )

  const selectedLabels = useMemo(() => {
    return labels.filter(label => label.id && selectedLabelIds.includes(label.id))
  }, [labels, selectedLabelIds])

  const renderValue = useCallback(() => {
    if (selectedLabels.length === 0) {
      return <Box color={Colors.base.middleGray}>選択してください</Box>
    }
    return (
      <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
        {selectedLabels.map(label => (
          <DocumentLabelItem key={label.id} label={label} />
        ))}
      </Box>
    )
  }, [selectedLabels])

  return (
    <FormControl variant="outlined" className={classes.formControl} fullWidth>
      <Select
        multiple
        classes={{ root: classes.selectRoot }}
        value={selectedLabelIds}
        onChange={handleChange}
        displayEmpty
        MenuProps={menuProps}
        renderValue={renderValue}
        inputProps={{
          name: name,
          id: `${name}-label`,
          'aria-label': 'Without label'
        }}
      >
        {labels.map((label, index) => (
          <MenuItem key={label.id || `label-${index}`} value={label.id} classes={{ root: classes.menuRoot }}>
            <Box display="flex" alignItems="center" py="5px" width="100%">
              <Checkbox
                checked={label.id ? selectedLabelIds.includes(label.id) : false}
                size="small"
                className={classes.checkbox}
              />
              <DocumentLabelItem label={label} />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
